import React, { useMemo, useState } from 'react';
import styles from './styles.module.css';
import { HeadingTag, Title } from '../Title';
import { Option } from '../SelectInput';
import { IoMdPin } from 'react-icons/io';
import { FaPhoneAlt, FaRegCalendar } from 'react-icons/fa';
import { Autocomplete } from '../Autocomplete';

export interface DepartmentSections {
  id: string;
  name: string;
  address?: string;
  postalCode?: string;
  district?: string;
  city?: string;
  state?: string;
  countryCode?: string;
  openingTime?: string;
  mapUrl?: string;
  active?: boolean;
  complement?: string;
  contact?: string;
}

export interface ServiceLocationProps {
  title: string;
  asTitle?: HeadingTag;
  departmentSections: DepartmentSections[];
  department: Option | null;
  setDepartment: (option: Option | null) => void;
  setCity: (option: Option | null) => void;
  mapUrl: string;
  city: Option | null;
  citySections: Option[];
}

const calculateDropdownHeight = (itemsCount: number) => {
  const cardInitialHeight = 292;
  const searchInputHeight = 37;
  const optionHeight = 40;
  const dropdownPadding = 8;
  const expandedPaddingHeight = 72; // Additional space when expanded
  const maxDropdownListHeight = 200; // Maximum height for dropdown list
  const minHeight = cardInitialHeight + expandedPaddingHeight;
  const maxHeight = cardInitialHeight + maxDropdownListHeight;

  const dropdownContentHeight =
    searchInputHeight + itemsCount * optionHeight + dropdownPadding;

  const totalHeight = cardInitialHeight + dropdownContentHeight;

  return Math.min(Math.max(totalHeight, minHeight), maxHeight);
};

export const ServiceLocation = ({
  title,
  asTitle = 'h2',
  departmentSections,
  department,
  setDepartment,
  mapUrl,
  city,
  citySections,
  setCity,
}: ServiceLocationProps) => {
  const selectedDepartment = departmentSections.find(
    (section) => section.id === department?.value
  );

  const [isOpenDepartment, setIsOpenDepartment] = useState(false);
  const [searchQueryDepartment, setSearchQueryDepartment] = useState('');

  const [isOpenCity, setIsOpenCity] = useState(false);
  const [searchQueryCity, setSearchQueryCity] = useState('');

  const options: Option[] = departmentSections.map((section) => ({
    label: section.name,
    value: section.id,
  }));

  useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQueryDepartment.toLowerCase())
    );
  }, [searchQueryCity, options]);

  useMemo(() => {
    return citySections.filter((option) =>
      option.label.toLowerCase().includes(searchQueryCity.toLowerCase())
    );
  }, [searchQueryDepartment, citySections]);

  const expandedHeight = useMemo(() => {
    if (isOpenDepartment && !department) {
      return calculateDropdownHeight(options.length);
    }
    return 'auto';
  }, [isOpenDepartment, department, options.length]);

  return (
    <div
      className={styles.cardBaseLocation}
      style={{
        height: isOpenDepartment ? `${expandedHeight}px` : 'auto',
      }}
    >
      <div className={styles.baseLocation}>
        <Title title={title} as={asTitle} />
        <Autocomplete
          label={'Selecione o município'}
          value={city}
          setValue={setCity}
          options={citySections}
          isOpen={isOpenCity}
          setIsOpen={setIsOpenCity}
          searchQuery={searchQueryCity}
          setSearchQuery={setSearchQueryCity}
        />
        <Autocomplete
          label={'Selecione a unidade de atendimento'}
          value={department}
          setValue={setDepartment}
          options={options}
          isOpen={isOpenDepartment}
          setIsOpen={setIsOpenDepartment}
          searchQuery={searchQueryDepartment}
          setSearchQuery={setSearchQueryDepartment}
        />
      </div>
      {selectedDepartment && (
        <>
          <div className={styles.divideLocation} />
          {mapUrl && (
            <div className={styles.locationMap}>
              <iframe
                src={mapUrl}
                title={`Mapa da localização da unidade ${selectedDepartment.name}`}
                aria-label={`Mapa da localização da unidade ${selectedDepartment.name}`}
                width="100%"
                height="220px"
                className={styles.map}
              />
            </div>
          )}
          <div className={styles.baseAddress}>
            <div className={styles.department}>{selectedDepartment.name}</div>
            <div className={styles.wrapAddress}>
              <span role="img" aria-label="Ícone de localização">
                <IoMdPin className={styles.icon} fontSize={16} />
              </span>
              <div>
                <div className={styles.subTitle}>Endereço</div>
                <div className={styles.typography}>
                  {selectedDepartment.address},
                  {selectedDepartment.complement
                    ? ` ${selectedDepartment.complement},`
                    : ''}{' '}
                  {selectedDepartment.district}, {selectedDepartment.city} /{' '}
                  {selectedDepartment.state},{' '}
                  {`CEP: ${selectedDepartment.postalCode}`}
                </div>
              </div>
            </div>
            {selectedDepartment.openingTime && (
              <div className={styles.wrapAddress}>
                <span role="img" aria-label="Ícone de calendário">
                  <FaRegCalendar className={styles.icon} fontSize={16} />
                </span>
                <div>
                  <div className={styles.subTitle}>Horário de atendimento</div>
                  <div className={styles.typography}>
                    {selectedDepartment.openingTime}
                  </div>
                </div>
              </div>
            )}
            {selectedDepartment.contact && (
              <div className={styles.wrapAddress}>
                <span role="img" aria-label="Ícone de telefone">
                  <FaPhoneAlt className={styles.icon} fontSize={16} />
                </span>
                <div>
                  <div className={styles.subTitle}>Telefone para contato</div>
                  <div className={styles.typography}>
                    {selectedDepartment.contact}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
